import { useCallback } from 'react';

/**
 * Report "the user is about to query this connection" so the main process can
 * open a connection ahead of time.
 *
 * Remote pools deliberately hold no standing connection — serverless engines
 * auto-pause, per-connection billing is real, a bastion has a session budget —
 * so the first query after a pause pays a full reconnect (~500-800ms over WAN).
 * The intent signals that beat it are cheap and already in the UI: the SQL
 * editor taking focus, the first keystroke in a tab, a Table Explorer tab
 * opening. Local connections keep a warm floor instead, and the call is a
 * harmless no-op for them.
 *
 * This is NOT a user-actionable surface — no button, no menu item, no visible
 * state change — so it deliberately fires no analytics event.
 *
 * The signals are chatty by nature (focus fires on every alt-tab back), so
 * dispatches are throttled per (connectionId, database). The throttle state is
 * module-level rather than per-hook: tabs mount, unmount and remount as the
 * user switches around, and a remount must not buy a fresh burst. The main
 * process applies the same window independently — this side only exists to keep
 * pointless IPC off the wire.
 */

/** One dispatch per (connectionId, database) per this window. Mirrors main. */
export const PREWARM_THROTTLE_MS = 20_000;

/** Last dispatch time per (connectionId, database), for the whole renderer. */
const lastPrewarmAt = new Map<string, number>();

/** Test seam — drop all throttle state. NOT for production use. */
export function _resetConnectionPrewarmThrottleForTest(): void {
  lastPrewarmAt.clear();
}

/**
 * Returns a `notePrewarmIntent()` callback. Calling it is always safe: it is
 * throttled, swallows every error, and does nothing at all without a
 * connection. The callback's identity changes only when the connection or
 * database changes, so it can sit in a keystroke handler's dependency list
 * without churning it.
 */
export function useConnectionPrewarm(
  connectionId: string | undefined,
  database: string | undefined
): () => void {
  return useCallback(() => {
    if (!connectionId) return;

    const key = `${connectionId}\u0000${database ?? ''}`;
    const now = Date.now();
    const last = lastPrewarmAt.get(key);
    if (last !== undefined && now - last < PREWARM_THROTTLE_MS) return;
    lastPrewarmAt.set(key, now);

    try {
      void Promise.resolve(
        window.electronAPI?.prewarmConnection?.(connectionId, database)
      ).catch(() => {
        // Prewarming is an optimisation. A rejected invoke (no handler in a
        // browser-hosted dev run, main busy, connection gone) costs nothing but
        // the reconnect the query would have paid anyway.
      });
    } catch {
      // `window.electronAPI` absent entirely — same story.
    }
  }, [connectionId, database]);
}
